/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React from "react";
import styled, { css } from "styled-components";
import Text from "./Text";

const StyledTableWrapper = styled.div`
  position: relative;
  width: 100%;
  overflow-x: auto;
`;

const StyledTable = styled.table`
  width: 100%;
  text-indent: 0;
  border: 1px solid rgba(${({ theme }) => theme.colors.border.rgba});
  border-collapse: collapse;
  background-color: rgba(${({ theme }) => theme.colors.background.rgba});
`;

const StyledTableRow = styled.tr`
  border-bottom: 1px solid rgba(${({ theme }) => theme.colors.border.rgba});
  transition: 0.2s ease;

  ${({ $clickable }) =>
    $clickable
      ? css`
          cursor: pointer;

          @media (hover: hover) {
            &:hover {
              box-shadow: 0px 2px 2px 0px rgba(0, 32, 51, 0.04), 0px 4px 16px 0px rgba(0, 32, 51, 0.18);
            }
          }
        `
      : css`
          @media (hover: hover) {
            &:hover {
              background-color: color-mix(in srgb, rgba(${({ theme }) => theme.colors.background.rgba}) 96%, black);
            }
          }
        `}

  &:last-child {
    border-bottom: none;
  }
`;

const StyledTableHeader = styled.th`
  padding: 0.5rem 1rem;
  text-align: left;
  background-color: color-mix(in srgb, rgba(${({ theme }) => theme.colors.background.rgba}) 94%, black);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  ${(props) => textAlignStyles[props.$align]}
`;

const StyledTableCell = styled.td`
  padding: 0.75rem 1rem;
  color: rgba(${({ theme }) => theme.colors.text.rgba});
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  ${(props) => textAlignStyles[props.$align]}
`;

const textAlignStyles = {
  left: css`
    text-align: left;
  `,
  center: css`
    text-align: center;
  `,
  right: css`
    text-align: right;
  `,
};

const Table = ({ children, ...props }) => {
  return (
    <StyledTableWrapper>
      <StyledTable {...props}>{children}</StyledTable>
    </StyledTableWrapper>
  );
};

const TableHeader = ({ children }) => {
  return <thead>{children}</thead>;
};

const TableBody = ({ children }) => {
  return <tbody>{children}</tbody>;
};

const TableRow = ({ children, clickable = false, onClick }) => {
  return (
    <StyledTableRow $clickable={clickable} onClick={onClick}>
      {children}
    </StyledTableRow>
  );
};

const TableHead = ({ children, align = "left" }) => {
  return (
    <StyledTableHeader $align={align}>
      <Text color="dark">{children}</Text>
    </StyledTableHeader>
  );
};

const TableCell = ({ children, align = "left", ...props }) => {
  return <StyledTableCell $align={align} {...props}>{children}</StyledTableCell>;
};

export { Table, TableHead, TableBody, TableRow, TableHeader, TableCell };
